import classNames from "classnames";
import { ScaleBand, ScaleLinear } from "d3";
import React, { useRef } from "react";

import { Action, ActionType } from "../../../../types";
import { ActionLineRenderer } from "../../../action-lines";
import { useMoveActionBar, useMoveActionHandle } from "../../hooks";
import styles from "./action-bar.module.scss";

export type ActionBarProps = {
  action: Action;
  playerScale: ScaleBand<string>;
  timelineScale: ScaleLinear<number, number>;
  canEdit: boolean;
};

export const BAR_HANDLE_WIDTH = 16;
const ACTION_LINE_LENGTH = 120;

export const ActionBar = ({ action, playerScale, timelineScale, canEdit }: ActionBarProps) => {
  const { actionType, startTime, endTime } = action;

  const actionBarRef = useRef<SVGGElement>(null);
  const startHandleRef = useRef<SVGRectElement>(null);
  const endHandleRef = useRef<SVGRectElement>(null);

  const barHeight = playerScale.bandwidth();

  const hasPossession = [ActionType.POSSESSION, ActionType.BALL].includes(actionType);

  const { tempX } = useMoveActionBar({
    action,
    x: timelineScale(startTime),
    scale: timelineScale,
    actionBarRef,
    canEdit,
  });

  const { tempXStart, tempXEnd, tempWidth } = useMoveActionHandle({
    action,
    xStart: 0,
    xEnd: timelineScale(endTime - startTime),
    scale: timelineScale,
    startHandleRef: startHandleRef,
    endHandleRef: endHandleRef,
    canEdit,
  });

  const width = tempWidth - 2;

  const lineStart = Math.max(width / 2 - ACTION_LINE_LENGTH / 2 + tempXStart, tempXStart + BAR_HANDLE_WIDTH * 2);
  const lineEnd = Math.min(width / 2 + ACTION_LINE_LENGTH / 2 + tempXStart, tempXEnd - BAR_HANDLE_WIDTH * 2);

  return (
    <g ref={actionBarRef} className={styles.actionBar} transform={`translate(${tempX}, 0)`}>
      <rect
        className={classNames(styles.actionBarFill, {
          [styles.possession || ""]: hasPossession,
          [styles.passFill || ""]: actionType === ActionType.BALL,
        })}
        x={tempXStart + BAR_HANDLE_WIDTH - 2}
        y={0}
        width={Math.max(width - BAR_HANDLE_WIDTH * 2 + 4, 0)}
        height={barHeight}
        ry={1}
      />
      <rect
        ref={actionType !== ActionType.BALL ? startHandleRef : undefined}
        className={classNames(styles.actionBarHandle, { [styles.possession || ""]: hasPossession })}
        x={tempXStart}
        y={0}
        width={BAR_HANDLE_WIDTH}
        height={barHeight}
        ry={2}
      />
      <rect
        ref={actionType !== ActionType.BALL ? endHandleRef : undefined}
        className={classNames(styles.actionBarHandle, { [styles.possession || ""]: hasPossession })}
        x={tempXEnd - BAR_HANDLE_WIDTH - 2}
        y={0}
        width={BAR_HANDLE_WIDTH}
        height={barHeight}
        ry={2}
      />

      {action.actionType !== ActionType.BALL && (
        <ActionLineRenderer
          action={{
            ...action,
            pos: { x: lineStart, y: barHeight / 2 },
            midPoints: [],
            endPos: { x: lineEnd, y: barHeight / 2 },
            color: action.actionType === ActionType.POSSESSION ? "#deab6f" : "#cdcdcd",
          }}
          strokeWidth={1.5}
          animateable={false}
        />
      )}
    </g>
  );
};
